import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ErrorStateMatcher, MatOption } from '@angular/material/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { BehaviorSubject } from 'rxjs';
import { ReferenceService } from '../../../../../../shared/reference/reference.service';
import { Logger } from '../../../../../../infrastructure/logging/logger';
import { Country } from '../../../../../../shared/address/Country';
import { State } from '../../../../../../shared/address/State';
import { CountryService } from '../../../../../../shared/reference/country.service';

export interface OrgAddressEditDialogData {
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  countryId: number;
  postalCode: string;
}

@Component({
  selector: 'app-org-address-edit-dialog',
  templateUrl: './org-address-edit-dialog.component.html',
  styleUrls: ['./org-address-edit-dialog.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatProgressSpinner,
    MatFormFieldModule,
    ReactiveFormsModule,
    MatInputModule,
    MatOption,
    MatSelectModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule
  ]
})
export class OrgAddressEditDialogComponent implements OnInit {

  formControl!: FormGroup;
  countries!: Country[];
  states!: State[];
  matcher = new ErrorStateMatcher();
  loading = new BehaviorSubject<boolean>(false);
  usCountrySelectedBoolean: boolean = true;

  constructor(
    public dialogRef: MatDialogRef<OrgAddressEditDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: OrgAddressEditDialogData,
    private referenceService: ReferenceService,
    private formBuilder: FormBuilder,
    private countryService: CountryService,
    private logService: Logger
  ) { }

  ngOnInit() {
    this.loading.next(true);

    this.formControl = this.formBuilder.group({
      addressLine1: [this.data.addressLine1, [Validators.required, Validators.pattern(/[\S]/)]],
      addressLine2: [this.data.addressLine2],
      city: [this.data.city, [Validators.required, Validators.pattern(/[\S]/)]],
      state: [this.data.state, [Validators.required, Validators.pattern(/[\S]/)]],
      country: [this.data.countryId, Validators.required],
      postalCode: [this.data.postalCode, [Validators.required, Validators.pattern(/[\S]/)]]
    });

    this.states = this.referenceService.getAllOrganizationAddressStates();

    if (this.data.countryId === 1) {
      this.usCountrySelectedBoolean = true;
    } else {
      this.usCountrySelectedBoolean = false;
    }

    try {
      this.countryService.getCountries().then(data => {
        this.countries = data;
      });
    } catch (e) {
      this.logService.handleError(e, 'An error occurred while retrieving Countries', 'Error while retrieving Countries');
    } finally {
      this.loading.next(false);
    } 
  }

  onSaveClick(): void {
    this.updateData()
    this.dialogRef.close(this.data);
  }

  updateData() {
    this.data.addressLine1 = this.formControl.controls['addressLine1'].value;
    this.data.addressLine2 = this.formControl.controls['addressLine2'].value;
    this.data.city = this.formControl.controls['city'].value;
    this.data.state = this.formControl.controls['state'].value;
    this.data.countryId = this.formControl.controls['country'].value;
    this.data.postalCode = this.formControl.controls['postalCode'].value;
  }

  onCloseClick(): void {
    this.dialogRef.close();
  }

  onCountrySelectionChange(event: MatSelectChange) {
    if (event.value === 1) {
      this.formControl.get('state')?.reset();
      this.formControl.get('state')?.setValidators([Validators.required]);
      this.formControl.get('state')?.updateValueAndValidity();
      this.usCountrySelectedBoolean = true;
    } else {
      this.formControl.get('state')?.reset();
      this.formControl.get('state')?.clearValidators();
      this.formControl.get('state')?.updateValueAndValidity();
      this.usCountrySelectedBoolean = false;
    }
  }

  canSaveOrganizationAddress(): boolean {
    if (this.usCountrySelectedBoolean) {
      let validStateBoolean: boolean = false;
      if (this.formControl.get('state')?.valid) {
        validStateBoolean = true;
      }

      if (validStateBoolean && this.formControl.valid) {
        return true;
      } else {
        return false;
      }
    } else {
      return (
        this.formControl.valid
      );
    }
  }
}